import {
  AppstoreOutlined,
  AuditOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CreditCardOutlined,
  FileAddOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuOutlined,
  SearchOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  ConfigProvider,
  Drawer,
  Grid,
  Layout as AntLayout,
  Menu,
  Space,
  Typography,
  Tag,
  theme as antdTheme,
  type MenuProps,
} from 'antd';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../api/appApi';
import { setToken } from '../api/httpClient';
import wordmark from '../assets/brand/uncle-advertisers-wordmark.svg';
import { APP_NAME } from '../constants';
import { useAuthToken } from '../lib/authToken';
import { toast } from '../lib/toast';
import { hasAccountsAccess, hasBillingAccess, hasCapability, hasSalesAccess, landingPath, type ZenCapability } from './access';
import { useZenBootstrap } from './hooks';
import { availableWorkForUser, newlyAvailableWork, playWorkNotificationSound, workNotificationMessage } from './workNotifications';
import { CommandPalette, ShortcutSheet } from './CommandPalette';
import { HeldActionsProvider } from './HeldActionsProvider';
import { useGlobalShortcuts } from './shortcuts';
import { LOGISTICS_DEPARTMENT_ID } from './status';
import { PageSkeleton } from './ui';

export function ZenProtectedRoute() {
  const token = useAuthToken();
  const { data, isLoading, isError } = useZenBootstrap();
  if (!token) return <Navigate to="/login" replace />;
  if (isLoading) {
    return (
      <div className="ua-boot">
        <PageSkeleton />
      </div>
    );
  }
  if (isError || !data?.currentUser) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function ZenAccessRoute({
  capability,
  children,
}: {
  capability: ZenCapability;
  children: React.ReactNode;
}) {
  const { data } = useZenBootstrap();
  if (!data) return null;
  const allowed = hasCapability(data.currentUser, data.departments, capability);
  return allowed ? <>{children}</> : <Navigate to="/" replace />;
}

export function ZenLandingRoute() {
  const { data } = useZenBootstrap();
  if (!data) return null;
  const destination = landingPath(data.currentUser, data.departments);
  return destination
    ? <Navigate to={destination} replace />
    : <Typography.Text type="secondary">No workspace has been assigned. Contact an administrator.</Typography.Text>;
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

function getActiveKey(pathname: string, keys: string[]): string {
  if (pathname === '/') return '/';
  const prefix = keys
    .filter((key) => key !== '/' && pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ?? '';
}

export function ZenShell() {
  const { data } = useZenBootstrap();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const openSheet = useCallback(() => setSheetOpen(true), []);
  useGlobalShortcuts({ openPalette, openSheet });
  const notificationUser = useRef<string | null>(null);
  const availableTaskIds = useRef<Set<string>>(new Set());
  const isMobile = !screens.md;

  useEffect(() => {
    if (!data) return;
    const currentAvailable = availableWorkForUser(data);
    const currentIds = new Set(currentAvailable.map((task) => task.id));

    if (notificationUser.current !== data.currentUser.id) {
      notificationUser.current = data.currentUser.id;
      availableTaskIds.current = currentIds;
      return;
    }

    const newTasks = newlyAvailableWork(availableTaskIds.current, data);
    availableTaskIds.current = currentIds;
    if (!newTasks.length) return;

    toast.info(workNotificationMessage(newTasks, data), 7_000);
    playWorkNotificationSound();
  }, [data]);

  if (!data) return null;
  const user = data.currentUser;
  const isAdmin = user.isAdmin;
  const isSales = hasSalesAccess(user);
  const isAccounts = hasAccountsAccess(user);
  const hasQueue = hasCapability(user, data.departments, 'queue');
  const hasDepartment = (id: string) => user.departmentIds.includes(id);
  const waiting = availableWorkForUser(data);
  const waitingLogistics = waiting.filter((task) => task.departmentId === LOGISTICS_DEPARTMENT_ID).length;
  const waitingQueue = waiting.length - waitingLogistics;
  const awaitingInvoice = data.workOrders.filter((order) => !order.deletedAt && order.status === 'active' && !order.accountsApproved).length;
  const withCount = (label: string, count: number): ReactNode => (
    <span className="sidebar-nav-label-with-badge">
      {label}
      {count > 0 && <span className="sidebar-nav-count" aria-label={`${count} waiting`}>{count}</span>}
    </span>
  );

  const signOut = async () => {
    try {
      await logout();
    } finally {
      setToken(null);
      navigate('/login', { replace: true });
    }
  };

  const menuItems: MenuProps['items'] = [
    ...(isAdmin || isAccounts
      ? [{ key: '/dashboard', icon: <AppstoreOutlined />, label: 'Dashboard' }]
      : []),
    ...(hasQueue
      ? [
          { key: '/queue', icon: <UnorderedListOutlined />, label: withCount('My queue', hasDepartment(LOGISTICS_DEPARTMENT_ID) ? waitingQueue : waiting.length) },
          ...(hasDepartment(LOGISTICS_DEPARTMENT_ID) ? [{ key: '/queue/logistics', icon: <UnorderedListOutlined />, label: withCount('Installation/Delivery queue', waitingLogistics) }] : []),
          { key: '/completed', icon: <CheckCircleOutlined />, label: 'Completed tasks' },
        ]
      : []),
    ...(isSales || isAdmin
      ? [{ key: '/work-orders/new', icon: <FileAddOutlined />, label: 'New work order' }]
      : []),
    ...(isSales || isAccounts || isAdmin
      ? [{ key: '/work-orders', icon: <FileTextOutlined />, label: 'Work orders' }]
      : []),
    ...(hasBillingAccess(user)
      ? [{ key: '/accounts', icon: <CreditCardOutlined />, label: withCount('Accounts & ledger', awaitingInvoice) }]
      : []),
    ...(isAdmin
      ? [
          { key: '/admin', icon: <SettingOutlined />, label: 'Administration' },
          { key: '/audit', icon: <AuditOutlined />, label: 'Audit log' },
        ]
      : []),
  ];
  const activeKey = getActiveKey(
    location.pathname,
    menuItems.flatMap((item) => (item && 'key' in item ? [String(item.key)] : [])),
  );

  const dateLabel = new Date().toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
  const handleNavigate = (path: string) => {
    navigate(path);
    setNavOpen(false);
  };
  const sidebarPanel = (
    <div className="zen-sidebar-panel">
      <div className="sidebar-brand zen-sidebar-brand">
        <div className="zen-brand-lockup">
          <img src={wordmark} alt="Uncle Advertisers" className="zen-brand-wordmark" />
          <span className="zen-brand-descriptor">Order management</span>
        </div>
        {isMobile && (
          <Button
            type="text"
            className="zen-sidebar-close"
            icon={<CloseOutlined />}
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
          />
        )}
      </div>
      <ConfigProvider
        theme={{
          algorithm: antdTheme.defaultAlgorithm,
          token: {
            colorPrimary: '#314F9C',
            colorBgContainer: 'transparent',
          },
        }}
      >
        <div className="sidebar-scroll">
          <Menu
            mode="inline"
            theme="light"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={({ key }) => handleNavigate(key)}
            className="sidebar-menu"
          />
        </div>
      </ConfigProvider>
      <div className="zen-sidebar-footer">
        <div className="zen-sidebar-utility zen-sidebar-date">
          <CalendarOutlined />
          <span>{dateLabel}</span>
        </div>
        <Button
          type="text"
          danger
          block
          className="zen-sidebar-logout"
          icon={<LogoutOutlined />}
          onClick={signOut}
        >
          Log out
        </Button>
      </div>
    </div>
  );

  return (
    <HeldActionsProvider>
    <AntLayout className="app-shell zen-shell">
      {!isMobile && (
        <AntLayout.Sider width="max-content" className="app-sidebar zen-sidebar" trigger={null}>
          {sidebarPanel}
        </AntLayout.Sider>
      )}
      {isMobile && (
        <Drawer
          placement="left"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          size="max-content"
          closable={false}
          className="zen-sidebar-drawer"
          rootClassName="zen-sidebar-drawer-root"
          styles={{
            body: {
              padding: 0,
              background: 'var(--sidebar-bg)',
            },
          }}
        >
          {sidebarPanel}
        </Drawer>
      )}

      <AntLayout className="app-main">
        <AntLayout.Header className="app-topbar">
          {isMobile && (
            <Button
              type="text"
              className="zen-mobile-menu"
              icon={<MenuOutlined />}
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
            />
          )}
          <h1 className="zen-page-title">{pageTitle(location.pathname)}</h1>
          <button type="button" className="ua-search-trigger" onClick={openPalette} aria-label="Search or jump to (Command K)">
            <SearchOutlined />
            <span className="ua-search-trigger-label">Search or jump to…</span>
            <kbd>⌘K</kbd>
          </button>
          <Space size={4} wrap className="zen-topbar-departments">
            {user.isAdmin && <Tag>Administrator</Tag>}
            {user.departmentNames.map((name) => <Tag key={name}>{name}</Tag>)}
          </Space>
          <Space size={4} className="topbar-actions" style={{ marginLeft: 'auto' }}>
            <Space size={8} style={{ padding: '0 4px' }}>
              <Avatar size={32} className="zen-user-avatar">
                {initials(user.name)}
              </Avatar>
              <div className="topbar-user-info">
                <div className="topbar-user-name">{user.name}</div>
                <div className="topbar-user-role">
                  {[...(user.isAdmin ? ['Administrator'] : []), ...user.departmentNames].join(' · ')}
                </div>
              </div>
            </Space>
          </Space>
        </AntLayout.Header>

        <AntLayout.Content className="page-content">
          <Outlet />
        </AntLayout.Content>
      </AntLayout>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </AntLayout>
    </HeldActionsProvider>
  );
}

function pageTitle(pathname: string): string {
  if (pathname === '/dashboard') return 'Today at the shop';
  if (pathname === '/queue/logistics') return 'Installation/Delivery queue';
  if (pathname === '/queue') return 'My queue';
  if (pathname === '/completed') return 'Completed tasks';
  if (pathname === '/work-orders/new') return 'Create work order';
  if (pathname === '/work-orders/completed') return 'Completed orders';
  if (pathname === '/work-orders/deleted') return 'Deleted orders';
  if (pathname.includes('/edit')) return 'Edit work order';
  if (pathname.startsWith('/work-orders/')) return 'Work order';
  if (pathname === '/work-orders') return 'Work orders';
  if (pathname === '/accounts') return 'Accounts & ledger';
  if (pathname.startsWith('/accounts')) return 'Customer ledger';
  if (pathname === '/admin') return 'Administration';
  if (pathname === '/audit') return 'Audit log';
  return APP_NAME;
}
